import { useCallback, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { TCreateCaseCommentBlock } from "../types";
import { createCaseCommentBlock } from "../helpers/createCaseCommentBlock";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectCaseValue } from "../redux/selectors";
import { CasesValue } from "../../../services/api/instances/casesApi/types";
import { setCaseValue } from "../redux/slice";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { generateId } from "../../../shared/utils/generateId";

export const useCaseCommentsBlock = () => {
  const dispatch = useAppDispatch();
  const caseValue = useAppSelector(selectCaseValue);
  const { selectedObject } = useStorageSelectors("cases");
  const [comments, setComments] = useState<TCreateCaseCommentBlock[]>([]);
  const [isCommentBlockOpen, setIsCommentBlockOpen] = useState<boolean>(false);

  const addCommentBlock = () => {
    const newCommentBlock = createCaseCommentBlock();
    setComments((prev) => [...prev, newCommentBlock]);
    setIsCommentBlockOpen(true);
  };

  const handleChangeBlockOpen = () => {
    setIsCommentBlockOpen(!isCommentBlockOpen);
  };

  const deleteCaseCommentBlock = (id: string) => {
    setComments((prev) => prev.filter((c) => c.id !== id));
  };

  const changeCommentValue = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: string,
    field: keyof Omit<TCreateCaseCommentBlock, "id">,
  ) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === id
          ? { ...comment, [field]: event.target.value }
          : comment,
      ),
    );
  };

  const saveComment = useCallback(
    (id: string) => {
      deleteCaseCommentBlock(id);
      const targetComment = comments.find((c) => c.id === id);
      if (!caseValue || !targetComment) return;
      const updatedValue: CasesValue = {
        ...caseValue,
        case_comments: [
          {
            id: `comment_${generateId()}`,
            case_comment_author: targetComment?.author ?? "",
            case_comment_data: new Date().toISOString(),
            case_comment: targetComment?.comment ?? "",
          },
          ...(caseValue?.case_comments ?? []),
        ],
      };
      dispatch(setCaseValue(updatedValue));
      dispatch(
        editStorageObject({
          id: selectedObject?.id ?? "",
          name: selectedObject?.friendly_name ?? "",
          value: updatedValue,
          service: "cases",
        }),
      );
    },
    [caseValue, comments, selectedObject?.id],
  );

  const handleDeleteComment = useCallback(
    (id: string) => {
      if (!caseValue) return;
      const updatedValue: CasesValue = {
        ...caseValue,
        case_comments: caseValue?.case_comments.filter((c) => c.id !== id),
      };
      dispatch(setCaseValue(updatedValue));
      dispatch(
        editStorageObject({
          id: selectedObject?.id ?? "",
          name: selectedObject?.friendly_name ?? "",
          value: updatedValue,
          service: "cases",
        }),
      );
    },
    [caseValue, selectedObject?.id],
  );

  return {
    comments,
    isCommentBlockOpen,
    handleChangeBlockOpen,
    addCommentBlock,
    changeCommentValue,
    deleteCaseCommentBlock,
    saveComment,
    handleDeleteComment,
  };
};
